/*
╔══════════════════════════════════════════════════════════════════════════════╗
║                  VUE.JS APPLICATION BOOTSTRAP - THE CORE TRIO                ║
╚══════════════════════════════════════════════════════════════════════════════╝

🚀 HOW A VUE.JS APP STARTS: THE THREE ESSENTIAL FILES

Every Vue.js application needs these three files working together:

┌─────────────────────────────────────────────────────────────────────────────┐
│ 1️⃣  index.html (in /public/)                                                │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ONLY actual HTML page in your entire app                             │
│ • Contains the <div id="app"></div> mounting point                         │
│ • Loads main.js via <script type="module">                                 │
│ • Stays the same for all routes - Vue handles the rest!                    │
│                                                                             │
│ Think of it as: The CONTAINER that holds everything                        │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ 2️⃣  main.js (in /src/)                                                      │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ENTRY POINT for your JavaScript application                          │
│ • Creates the Vue app instance: createApp(App)                             │
│ • Configures plugins (router, store, etc.)                                 │
│ • Mounts the app to #app: .mount('#app')                                   │
│                                                                             │
│ Think of it as: The ENGINE that starts everything                          │
└─────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────┐
│ 3️⃣  App.vue (in /src/)                                                      │
├─────────────────────────────────────────────────────────────────────────────┤
│ • The ROOT COMPONENT of your application                                   │
│ • Contains the main layout structure (header, nav, footer)                 │
│ • Houses <router-view> where pages are dynamically loaded                  │
│ • Wraps all other components in your app                                   │
│                                                                             │
│ Think of it as: The SKELETON that holds your app structure                 │
└─────────────────────────────────────────────────────────────────────────────┘

🔄 THE STARTUP SEQUENCE:

  Browser loads → index.html
                     ↓
                  Executes main.js
                     ↓
                  Creates Vue app with App.vue
                     ↓
                  Mounts to <div id="app">
                     ↓
                  App.vue renders with router
                     ↓
                  Your application is LIVE! ✨

💡 KEY CONCEPTS:

  • Single Page Application (SPA): Only ONE HTML file (index.html)
  • Component-Based: App.vue is the root, everything else nests inside
  • Reactive Mounting: Vue replaces <div id="app"> with your App component
  • Router Magic: <router-view> swaps pages WITHOUT reloading the browser

📚 LEARN MORE: https://vuejs.org/guide/essentials/application.html

*/



import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import axios from 'axios';

import './assets/main.css'

// Basic axios configuration
axios.defaults.baseURL = process.env.VUE_APP_API_URL;

// Response interceptor for handling auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored auth data on unauthorized
      localStorage.clear();
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        router.push('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Create Vue app instance
const app = createApp(App);

// Make axios available globally
app.config.globalProperties.$axios = axios;

// Use router
app.use(router);

// Mount the app
app.mount('#app');

// Log environment information
console.log('BACKEND API URL:', process.env.VUE_APP_API_URL);
console.log('Environment:', process.env.NODE_ENV);